import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, CheckCircle2, ArrowRight, Video, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const monthName = (m) => ["January","February","March","April","May","June","July","August","September","October","November","December"][m];
const dayLabel = (d) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()];

// Build 14 upcoming weekdays starting tomorrow
function upcomingWeekdays(count = 14) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1); // start tomorrow
  while (out.length < count) {
    if (d.getDay() !== 0 && d.getDay() !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Advisor() {
  const [step, setStep] = useState(0); // 0: start, 1: date, 2: time, 3: form, 4: confirmed
  const days = useMemo(() => upcomingWeekdays(14), []);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (!date) return;
    setSlots([]);
    setLoadingSlots(true);
    api.get("/bookings/availability", { params: { date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => toast.error("Could not load slots. Please try again."))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!date || !time) return;
    if (!form.name || !form.email) { toast.error("Name and email required."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { date, time, ...form });
      setConfirmation(data);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed. Try another slot.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-advisor">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        {step < 4 && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="rule-gold" /><span className="eyebrow">Free · 15 min · No commitment</span>
            </div>
            <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl" data-testid="advisor-headline">
              Book Your <em className="italic text-[#b8892b]">Free 15-Minute</em> Consultation.
            </h1>
            <p className="text-lg text-[#4a5570] mt-6 max-w-2xl leading-relaxed">
              Not sure where to start? Book a free 15-minute call with a FrancePath advisor to discuss your study goals, answer your questions, and explore your options for studying in France.
            </p>

            {/* Stepper */}
            <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-[#4a5570]" data-testid="advisor-stepper">
              {["Pick a day", "Pick a time", "Your details"].map((label, i) => (
                <div key={label} className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center text-[10px] ${step > i ? "bg-[#b8892b] text-white" : step === i + 1 ? "bg-[#0d1b3e] text-white" : "bg-[#0d1b3e]/10 text-[#4a5570]"}`}>
                    {step > i ? "✓" : i + 1}
                  </span>
                  <span className={step === i + 1 ? "text-[#0d1b3e]" : ""}>{label}</span>
                  {i < 2 && <span className="w-6 h-px bg-[#0d1b3e]/20 mx-1" />}
                </div>
              ))}
            </div>
          </>
        )}

        <AnimatePresence mode="wait">
          {/* STEP 0: intro */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="mt-14">
              <button onClick={() => setStep(1)} data-testid="advisor-book-cta"
                className="btn-sharp btn-sharp-gold text-base">
                Book My Free Call <ArrowRight size={16} />
              </button>
              <div className="mt-4 text-xs uppercase tracking-[0.22em] text-[#4a5570]" data-testid="advisor-reassurance">
                100% free · 15 minutes · No commitment
              </div>
              <div className="mt-16 border-t border-[#0d1b3e]/10 pt-8 text-sm text-[#4a5570] max-w-2xl">
                Prefer to fill a questionnaire first and get matched to universities? Try our{" "}
                <Link to="/plan-builder" className="underline decoration-[#b8892b] text-[#0d1b3e]" data-testid="plan-builder-link">
                  free 4-question plan builder
                </Link>.
              </div>
            </motion.div>
          )}

          {/* STEP 1: pick day */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="mt-14">
              <div className="flex items-center gap-2 mb-4 text-[#b8892b]">
                <CalendarDays size={16} /><span className="eyebrow !text-[#b8892b]">Pick a day</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="day-picker">
                {days.map((d) => {
                  const iso = isoDay(d);
                  const selected = date === iso;
                  return (
                    <button
                      key={iso}
                      onClick={() => { setDate(iso); setTime(null); setStep(2); }}
                      data-testid={`day-${iso}`}
                      className={`border p-3 text-left transition-colors ${selected ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "border-[#0d1b3e]/15 hover:bg-[#f5f1e8]"}`}
                    >
                      <div className={`text-[10px] uppercase tracking-[0.18em] ${selected ? "text-[#b8892b]" : "text-[#4a5570]"}`}>{dayLabel(d)}</div>
                      <div className={`serif text-2xl mt-1 ${selected ? "text-white" : "text-[#0d1b3e]"}`}>{d.getDate()}</div>
                      <div className={`text-[10px] uppercase tracking-[0.14em] mt-1 ${selected ? "text-white/60" : "text-[#4a5570]"}`}>{monthName(d.getMonth()).slice(0, 3)}</div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-6 text-xs text-[#4a5570]">All times shown in Europe/Paris. Weekends unavailable.</p>
            </motion.div>
          )}

          {/* STEP 2: pick time */}
          {step === 2 && date && (
            <motion.div key="s2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="mt-14">
              <button onClick={() => setStep(1)} data-testid="back-to-day" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4a5570] hover:text-[#0d1b3e] mb-6">
                <ArrowLeft size={14} /> Change day
              </button>
              <div className="flex items-center gap-2 mb-4 text-[#b8892b]">
                <Clock size={16} /><span className="eyebrow !text-[#b8892b]">Pick a time on {date}</span>
              </div>
              {loadingSlots ? (
                <div className="text-sm text-[#4a5570]">Loading slots…</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="time-picker">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      disabled={!s.available}
                      onClick={() => { setTime(s.time); setStep(3); }}
                      data-testid={`time-${s.time}`}
                      className={`border py-3 text-sm transition-colors ${!s.available ? "border-[#0d1b3e]/10 text-[#4a5570]/40 line-through cursor-not-allowed" : time === s.time ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "border-[#0d1b3e]/15 text-[#0d1b3e] hover:bg-[#0d1b3e] hover:text-white"}`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 3: details form */}
          {step === 3 && date && time && (
            <motion.div key="s3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="mt-14">
              <button onClick={() => setStep(2)} data-testid="back-to-time" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4a5570] hover:text-[#0d1b3e] mb-6">
                <ArrowLeft size={14} /> Change time
              </button>
              <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                  <div className="eyebrow mb-2">Your call</div>
                  <div className="serif text-3xl md:text-4xl text-[#0d1b3e] leading-tight">{date} at {time}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#4a5570] mt-2">Europe/Paris · 15 minutes · Free</div>
                </div>
                <form onSubmit={submitBooking} className="md:col-span-7 bg-white border border-[#0d1b3e]/10 p-6 md:p-8 space-y-4" data-testid="booking-form">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Full name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="booking-name"
                      className="mt-1 w-full border border-[#0d1b3e]/20 bg-white px-3 py-2 outline-none focus:border-[#b8892b]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Email</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="booking-email"
                      className="mt-1 w-full border border-[#0d1b3e]/20 bg-white px-3 py-2 outline-none focus:border-[#b8892b]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">What would you like to discuss? (optional)</label>
                    <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="booking-notes"
                      className="mt-1 w-full border border-[#0d1b3e]/20 bg-white px-3 py-2 outline-none focus:border-[#b8892b]" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid="booking-submit"
                    className="btn-sharp btn-sharp-gold w-full justify-center">
                    {submitting ? "Booking…" : "Confirm my free call"}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* STEP 4: confirmation */}
          {step === 4 && confirmation && (
            <motion.div key="s4" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="mt-6" data-testid="booking-confirmation">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule-gold" /><span className="eyebrow">Confirmed</span>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#b8892b] text-white flex items-center justify-center shrink-0">
                  <CheckCircle2 size={22} />
                </div>
                <div>
                  <h1 className="serif text-4xl md:text-6xl leading-[1] tracking-tight text-[#0d1b3e]">
                    You're booked, <em className="italic text-[#b8892b]">à bientôt.</em>
                  </h1>
                  <p className="mt-4 text-lg text-[#4a5570] max-w-2xl leading-relaxed">
                    A confirmation email has just been sent to <span className="font-semibold text-[#0d1b3e]">{form.email}</span> with the meeting link and details.
                  </p>
                </div>
              </div>

              <div className="mt-10 grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-[#0d1b3e]/10 p-6">
                  <div className="eyebrow">When</div>
                  <div className="serif text-3xl text-[#0d1b3e] mt-1" data-testid="confirmation-datetime">{confirmation.date} at {confirmation.time}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#4a5570] mt-1">{confirmation.timezone} · 15 minutes</div>
                </div>
                <div className="bg-[#0d1b3e] text-white p-6">
                  <div className="eyebrow !text-[#b8892b]">How to join</div>
                  <div className="serif text-xl mt-1">Click the link at the scheduled time.</div>
                  <a
                    href={confirmation.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="confirmation-meeting-link"
                    className="btn-sharp btn-sharp-gold mt-4 inline-flex"
                  >
                    <Video size={14} /> Open meeting link
                  </a>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/plan-builder" className="btn-sharp btn-sharp-outline" data-testid="confirmation-plan-builder">
                  Meanwhile, try the plan builder
                </Link>
                <Link to="/prepare" className="btn-sharp btn-sharp-outline" data-testid="confirmation-prepare">
                  Read the Prepare guide
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
